import dayjs from "dayjs";
import "dayjs/locale/es";

import {
  Invoice,
  InvoiceCompanyHeaderInfo,
} from "../../../bitmaker/schemas/Billing/Invoice";

import { formatCurrency, formatClientInformation } from "../../../utils";

import { InvoiceHeader } from "./Invoice/InvoiceHeader";
import { InvoiceLogo } from "./Invoice/InvoiceLogo";

interface InvoiceDisplayProps {
  invoice: Invoice;
  headerInfo: InvoiceCompanyHeaderInfo | null;
}

export const InvoiceDisplay: React.FC<InvoiceDisplayProps> = ({
  invoice,
  headerInfo,
}) => {
  if (!headerInfo) return null;

  const addressLines = formatClientInformation(invoice);

  const currentLanguage = invoice.language || "en";

  const texts = {
    item: currentLanguage === "es" ? "Ítem" : "Item",
    quantity: currentLanguage === "es" ? "Cantidad" : "Quantity",
    unitPrice: currentLanguage === "es" ? "Precio Unitario" : "Unit Price",
    total: currentLanguage === "es" ? "Total" : "Total",
    billedTo: currentLanguage === "es" ? "Facturado a:" : "Billed To:",
    issueDate: currentLanguage === "es" ? "Fecha de Emisión:" : "Issue Date:",
    dueDate: currentLanguage === "es" ? "Fecha de Vencimiento:" : "Due Date:",
    reference: currentLanguage === "es" ? "Referencia:" : "Reference:",
    invoice: currentLanguage === "es" ? "FACTURA" : "INVOICE",
    amountDue: currentLanguage === "es" ? "Total a Pagar" : "Amount Due",
    notes: currentLanguage === "es" ? "Notas:" : "Notes:",
    terms: currentLanguage === "es" ? "Términos:" : "Terms:",
    subtotal: currentLanguage === "es" ? "Subtotal" : "Subtotal",
    tax: currentLanguage === "es" ? "Impuesto" : "Tax",
    amountPaid: currentLanguage === "es" ? "Monto Pagado" : "Amount Paid",
  };

  const formatDate = (date: any) =>
    dayjs(date)
      .locale(currentLanguage)
      .format(
        currentLanguage === "es" ? "D [de] MMMM [de] YYYY" : "MMMM D, YYYY",
      );

  const renderItemsTable = () => (
    <table className="w-full mt-8 border-collapse">
      <thead>
        <tr className="bg-gray-100 border-b border-t">
          <th className="text-left p-2">{texts.item}</th>
          <th className="p-2 w-20 text-right">{texts.quantity}</th>
          <th className="p-2 w-28 text-right">{texts.unitPrice}</th>
          <th className="p-2 w-28 text-right">{texts.total}</th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item, index) => (
          <tr key={index} className="border-b">
            <td className="p-2 align-top">
              <p className="font-medium">{item.itemName}</p>
              {item.itemDescription && (
                <p className="text-xs text-gray-500 whitespace-pre-line">
                  {item.itemDescription}
                </p>
              )}
            </td>
            <td className="p-2 text-right align-top">{item.quantity}</td>
            <td className="p-2 text-right align-top">
              {formatCurrency(
                item.unitPrice,
                invoice.currency,
                currentLanguage,
              )}
              {item.appliedTaxes && item.appliedTaxes.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {item.appliedTaxes.map((tax, taxIndex) => (
                    <p key={taxIndex} className="leading-none">
                      (+{tax.taxName})
                    </p>
                  ))}
                </div>
              )}
            </td>
            <td className="p-2 text-right font-semibold align-top">
              {formatCurrency(
                item.lineSubtotal,
                invoice.currency,
                currentLanguage,
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="min-h-full">
      <InvoiceHeader displayInfo={headerInfo} />

      <div className="px-10 pb-12 mb-10">
        <InvoiceLogo />

        <div className="grid grid-cols-12 gap-8 mt-10">
          <div className="col-span-6">
            <h3 className="text-sm font-bold uppercase text-gray-500 mb-2">
              {texts.billedTo}
            </h3>
            {addressLines.map((line, index) => (
              <p key={index} className={index === 0 ? "font-semibold" : ""}>
                {line}
              </p>
            ))}

            <div className="mt-6 text-sm">
              <p>
                <strong>{texts.issueDate} </strong>
                {formatDate(invoice.issueDate)}
              </p>
              <p>
                <strong>{texts.dueDate} </strong>
                {formatDate(invoice.dueDate)}
              </p>
              {invoice.reference && (
                <p>
                  <strong>{texts.reference} </strong>
                  {invoice.reference}
                </p>
              )}
            </div>
          </div>

          <div className="col-span-2"></div>

          <div className="col-span-4 bg-gray-50 p-4 rounded-lg self-start">
            <p className="font-bold uppercase">
              {texts.invoice} #{invoice.invoiceNumber}
            </p>
            <h3 className="font-bold uppercase mt-4">
              {texts.amountDue} ({invoice.currency}):
            </h3>
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(
                invoice.totalAmount - invoice.amountPaid,
                invoice.currency,
                currentLanguage,
              )}
            </p>
          </div>
        </div>

        <div className="mt-10">{renderItemsTable()}</div>

        <div className="grid grid-cols-12 mt-10">
          {/* Notes & Terms */}
          <div className="col-span-6">
            {invoice.notes && (
              <>
                <h4 className="font-semibold mb-2">{texts.notes}</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {invoice.notes}
                </p>
              </>
            )}

            {invoice.terms && (
              <>
                <h4
                  className={`font-semibold mb-2 ${invoice.notes ? "mt-4" : ""
                    }`}
                >
                  {texts.terms}
                </h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {invoice.terms}
                </p>
              </>
            )}
          </div>

          <div className="col-span-2"></div>

          {/* Summary Totals */}
          <div className="col-span-4">
            <div className="flex flex-col text-right">
              {/* Subtotal */}
              <div className="flex justify-between items-center font-semibold">
                <span className="text-gray-700">{texts.subtotal}</span>
                <span>
                  {formatCurrency(
                    invoice.subtotal,
                    invoice.currency,
                    currentLanguage,
                  )}
                </span>
              </div>

              {/* Tax */}
              {invoice.taxSummary && invoice.taxSummary.length > 0 ? (
                invoice.taxSummary.map((taxEntry, index) => {
                  const percentage = (taxEntry.taxRate * 100).toFixed(0);
                  const taxLabel = `${taxEntry.taxName} (${percentage}%)`;

                  return (
                    <div
                      key={index}
                      className="flex justify-between items-center font-semibold"
                    >
                      <span className="text-gray-700">{taxLabel}</span>
                      <span>
                        {formatCurrency(
                          taxEntry.totalTaxAmount,
                          invoice.currency,
                          currentLanguage,
                        )}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="flex justify-between items-center font-semibold">
                  <span className="text-gray-700">{texts.tax}</span>
                  <span>
                    {formatCurrency(0, invoice.currency, currentLanguage)}
                  </span>
                </div>
              )}

              <hr className="my-2 border-gray-300" />

              {/* Total */}
              <div className="flex justify-between items-center font-semibold">
                <span className="text-gray-700">{texts.total}</span>
                <span>
                  {formatCurrency(
                    invoice.totalAmount,
                    invoice.currency,
                    currentLanguage,
                  )}
                </span>
              </div>

              {/* Amount Paid */}
              <div className="flex justify-between items-center font-semibold">
                <span className="text-gray-700">{texts.amountPaid}</span>
                <span>
                  {formatCurrency(
                    invoice.amountPaid,
                    invoice.currency,
                    currentLanguage,
                  )}
                </span>
              </div>

              <hr className="my-2 border-gray-300" />

              {/* Amount Due */}
              <div className="flex justify-between items-center text-lg font-bold text-gray-900">
                <span>{texts.amountDue}</span>
                <span className="text-green-600">
                  {formatCurrency(
                    invoice.totalAmount - invoice.amountPaid,
                    invoice.currency,
                    currentLanguage,
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
